import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import Datetime from "react-datetime";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  getPortalAuditLogs,
  clearPortalAuditLogs,
} from "../../redux/actions/adminPortal_portalAuditLogs";
import { StyledDataGrid } from "../../pages/CustomDataGrid";
import PageTitle from "../common/PageTitle";

const MuiTheme = createTheme({
  components: {
    MuiDataGrid: {
      defaultProps: { density: "compact" },
    },
  },
});

const useStyles = makeStyles({
  borderedGreen: {
    borderLeft: "3px solid green",
    paddingLeft: "4px",
  },
  borderedRed: {
    borderLeft: "3px solid red",
    paddingLeft: "4px",
  },
});

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const ACTION_COLORS = {
  CREATED: "success",
  UPDATED: "info",
  DELETED: "error",
  SUSPENDED: "warning",
  ACTIVATED: "success",
  LOGIN_SUCCESS: "success",
  LOGIN_FAILED: "error",
  ACCOUNT_LOCKED: "error",
  PASSWORD_CHANGED: "warning",
  PASSWORD_RESET_REQUESTED: "info",
  PASSWORD_RESET_COMPLETED: "warning",
  PASSWORD_RESET_FAILED: "error",
  PERM_GRANTED: "success",
  PERM_REVOKED: "error",
  HANGUP: "warning",
};

const ENTITY_TYPES = [
  "ALL", "DID", "CAMPAIGN", "BUYER", "USER", "EXTENSION",
  "CARRIER", "DID_CARRIER", "CALL_BLOCK", "BILLING", "QUEUE", "QUEUE_MEMBER", "RECORDING", "MOH",
  "AUTH", "INVOICE", "ACL",
  "ROLE_PERM", "CALL_HANGUP", "KYC_DOC",
  "PRODUCT", "PROMOTIONAL",
];

const ACTIONS = [
  "ALL", "CREATED", "UPDATED", "DELETED", "SUSPENDED", "ACTIVATED",
  "LOGIN_SUCCESS", "LOGIN_FAILED", "ACCOUNT_LOCKED",
  "PASSWORD_CHANGED", "PASSWORD_RESET_REQUESTED", "PASSWORD_RESET_COMPLETED", "PASSWORD_RESET_FAILED",
  "PERM_GRANTED", "PERM_REVOKED", "HANGUP",
];

const AUDIT_COVERAGE = [
  {
    category: "Authentication & Passwords",
    color: "#d32f2f",
    items: [
      { entity: "AUTH", actions: "LOGIN_SUCCESS / LOGIN_FAILED / ACCOUNT_LOCKED", desc: "Every login attempt including failed passwords and auto-locks" },
      { entity: "USER", actions: "PASSWORD_CHANGED", desc: "Self-service admin password change" },
      { entity: "USER", actions: "PASSWORD_RESET_REQUESTED / _COMPLETED / _FAILED", desc: "Forgot-password OTP requests, successful resets, bad-OTP attempts" },
    ],
  },
  {
    category: "User & Access Management",
    color: "#ed6c02",
    items: [
      { entity: "USER", actions: "CREATED / UPDATED / SUSPENDED / ACTIVATED / DELETED", desc: "Admin user lifecycle including bulk suspend/activate" },
      { entity: "ROLE_PERM", actions: "PERM_GRANTED / PERM_REVOKED", desc: "Role permission toggle — logs with role and permission names resolved" },
    ],
  },
  {
    category: "Billing & Financial",
    color: "#2e7d32",
    items: [
      { entity: "BILLING", actions: "CREATED / UPDATED / SUSPENDED / ACTIVATED", desc: "Minute assignments, topups, active-toggle with delta in description" },
      { entity: "INVOICE", actions: "CREATED / UPDATED", desc: "Invoice creation and updates with item count" },
      { entity: "PRODUCT", actions: "CREATED / UPDATED", desc: "Product catalog (name, price, description)" },
      { entity: "PROMOTIONAL", actions: "CREATED", desc: "Promotional broadcasts — one row per push, lists recipients and files" },
    ],
  },
  {
    category: "Call Flow & Routing",
    color: "#0288d1",
    items: [
      { entity: "CAMPAIGN", actions: "CREATED / UPDATED / DELETED", desc: "Redirect groups / campaigns (admin and user-side)" },
      { entity: "BUYER", actions: "CREATED / UPDATED / DELETED", desc: "Redirect group details / buyers" },
      { entity: "DID", actions: "CREATED / UPDATED / SUSPENDED / ACTIVATED", desc: "DID management including bulk operations" },
      { entity: "DID_CARRIER", actions: "CREATED / UPDATED", desc: "DID carrier catalog" },
      { entity: "CARRIER", actions: "CREATED / UPDATED / DELETED", desc: "PJSIP trunk configuration" },
      { entity: "EXTENSION", actions: "CREATED / UPDATED / DELETED", desc: "SIP extensions (admin and user-side, password scrubbed)" },
      { entity: "QUEUE", actions: "CREATED / UPDATED", desc: "Queue config: strategy, music-on-hold, ring timeout" },
      { entity: "QUEUE_MEMBER", actions: "CREATED / DELETED", desc: "Queue membership add (batch) and remove" },
      { entity: "MOH", actions: "CREATED / UPDATED", desc: "Music-on-hold playlists with before/after entry list" },
      { entity: "RECORDING", actions: "CREATED / UPDATED", desc: "IVR recording uploads and file replacements (filename only)" },
      { entity: "CALL_HANGUP", actions: "HANGUP", desc: "Admin-triggered live-call disconnect" },
    ],
  },
  {
    category: "Security & Compliance",
    color: "#9c27b0",
    items: [
      { entity: "CALL_BLOCK", actions: "CREATED / DELETED", desc: "Blocked-number management (admin and user-side)" },
      { entity: "ACL", actions: "CREATED / DELETED", desc: "IP whitelist / blacklist changes" },
      { entity: "KYC_DOC", actions: "CREATED / UPDATED / DELETED", desc: "KYC identity documents — filenames only, never file contents" },
    ],
  },
];

const drawerWidth = 240;

const AdminPortalAuditLogs = ({ colorThem }) => {
  const dispatch = useDispatch();
  const { portalAuditLogs, loading } = useSelector(
    (state) => state.getPortalAuditLogs
  );

  const [fromDate, setFromDate] = useState(moment().startOf("day"));
  const [toDate, setToDate] = useState(moment().endOf("day"));
  const [entityType, setEntityType] = useState("ALL");
  const [action, setAction] = useState("ALL");
  const [username, setUsername] = useState("");
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(false);

  // Clear-logs dialog state — admin-only destructive flow.
  // Requires both a valid cutoff date AND user typing "DELETE" verbatim.
  const CLEAR_CONFIRM_PHRASE = "DELETE";
  const [clearOpen, setClearOpen] = useState(false);
  const [clearBeforeDate, setClearBeforeDate] = useState(
    moment().subtract(30, "days").startOf("day")
  );
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState("");

  const handleSearch = () => {
    const data = {
      from_date: moment(fromDate).format("YYYY-MM-DD HH:mm"),
      to_date: moment(toDate).format("YYYY-MM-DD HH:mm"),
    };
    if (entityType !== "ALL") data.entity_type = entityType;
    if (action !== "ALL") data.action = action;
    if (username.trim()) data.username = username.trim();
    dispatch(getPortalAuditLogs(data));
  };

  const handleReset = () => {
    setFromDate(moment().startOf("day"));
    setToDate(moment().endOf("day"));
    setEntityType("ALL");
    setAction("ALL");
    setUsername("");
    dispatch(
      getPortalAuditLogs({
        from_date: moment().startOf("day").format("YYYY-MM-DD HH:mm"),
        to_date: moment().endOf("day").format("YYYY-MM-DD HH:mm"),
      })
    );
  };

  useEffect(() => {
    handleSearch();
  }, []);

  const handleClearOpen = () => {
    setClearBeforeDate(moment().subtract(30, "days").startOf("day"));
    setClearConfirmText("");
    setClearError("");
    setClearOpen(true);
  };

  const handleClearClose = () => {
    if (clearLoading) return;
    setClearOpen(false);
    setClearConfirmText("");
    setClearError("");
  };

  const isClearDateValid =
    moment.isMoment(clearBeforeDate) &&
    clearBeforeDate.isValid() &&
    clearBeforeDate.isSameOrBefore(moment());

  const isClearArmed =
    isClearDateValid && clearConfirmText === CLEAR_CONFIRM_PHRASE && !clearLoading;

  const handleClearSubmit = () => {
    if (!isClearArmed) return;
    const beforeStr = clearBeforeDate.format("YYYY-MM-DD HH:mm:ss");
    const proceed = window.confirm(
      `Final confirmation:\n\nPermanently delete ALL portal audit log entries created before ${beforeStr}?\n\nThis cannot be undone. Click OK to proceed, Cancel to abort.`
    );
    if (!proceed) return;
    setClearLoading(true);
    setClearError("");
    dispatch(
      clearPortalAuditLogs(
        beforeStr,
        () => {
          setClearLoading(false);
          setClearOpen(false);
          setClearConfirmText("");
          handleSearch();
        },
        (msg) => {
          setClearLoading(false);
          setClearError(msg || "Failed to clear logs");
        }
      )
    );
  };

  const rows = useMemo(() => {
    if (!portalAuditLogs || !Array.isArray(portalAuditLogs)) return [];
    return portalAuditLogs.map((log, index) => ({
      id: index + 1,
      ...log,
    }));
  }, [portalAuditLogs]);

  const handleRowClick = (params) => {
    const row = params.row;
    if (row.before_state || row.after_state) {
      setSelectedRow(row);
      setDiffDialogOpen(true);
    }
  };

  const columns = [
    {
      field: "created_at",
      headerName: "Date/Time",
      width: 160,
      renderCell: (params) => {
        if (!params.value) return "";
        const m = moment(params.value);
        return (
          <span>
            <span style={{ color: "#1565c0" }}>{m.format("DD/MM/YYYY")}</span>{" "}
            <span style={{ color: "green" }}>{m.format("HH:mm:ss")}</span>
          </span>
        );
      },
    },
    {
      field: "user_name",
      headerName: "User",
      width: 120,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={ACTION_COLORS[params.value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "entity_type",
      headerName: "Entity",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="filled" />
      ),
    },
    {
      field: "entity_id",
      headerName: "Entity ID",
      width: 130,
      renderCell: (params) => (
        <span style={{ color: "#1565c0" }}>{params.value}</span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "changed_fields",
      headerName: "Changes",
      width: 100,
      renderCell: (params) => {
        const fields = params.value;
        if (!fields || fields.length === 0) return "---";
        return (
          <Chip
            label={`${fields.length} field(s)`}
            size="small"
            color="primary"
            variant="outlined"
            style={{ cursor: "pointer" }}
          />
        );
      },
    },
    {
      field: "source_ip",
      headerName: "IP",
      width: 130,
      renderCell: (params) => (
        <span style={{ color: "#1565c0" }}>{params.value}</span>
      ),
    },
  ];

  const handleExportCSV = () => {
    if (!rows || rows.length === 0) return;

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      let s = typeof val === "string" ? val : JSON.stringify(val);
      if (/[",\n\r]/.test(s)) {
        s = `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const m = moment(dateStr);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : "";
    };

    const headers = [
      "Date/Time", "User", "Action", "Entity Type", "Entity ID",
      "Description", "Changed Fields", "Before State", "After State", "Source IP",
    ];

    const lines = rows.map((r) => [
      formatDate(r.created_at),
      r.user_name || "",
      r.action || "",
      r.entity_type || "",
      r.entity_id || "",
      r.description || "",
      Array.isArray(r.changed_fields) ? r.changed_fields.join("; ") : (r.changed_fields || ""),
      r.before_state ? (typeof r.before_state === "string" ? r.before_state : JSON.stringify(r.before_state)) : "",
      r.after_state ? (typeof r.after_state === "string" ? r.after_state : JSON.stringify(r.after_state)) : "",
      r.source_ip || "",
    ].map(escape).join(","));

    const csvWithBOM = "\uFEFF" + headers.join(",") + "\n" + lines.join("\n");
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = moment().format("YYYYMMDD_HHmmss");
    a.download = `portal_audit_log_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseDiffData = (row) => {
    if (!row) return [];
    let before = {};
    let after = {};
    try {
      before = typeof row.before_state === "string" ? JSON.parse(row.before_state) : row.before_state || {};
      after = typeof row.after_state === "string" ? JSON.parse(row.after_state) : row.after_state || {};
    } catch (e) {
      return [];
    }
    const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return allKeys.map((key) => ({
      field: key,
      before: before[key] !== undefined ? String(before[key]) : "---",
      after: after[key] !== undefined ? String(after[key]) : "---",
    }));
  };

  return (
    <div className={`App ${colorThem}`}>
      <Box
        className="right_sidebox mobile_top_pddng"
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {/* Title row — Clear Logs button right-aligned next to the title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PageTitle title="Portal Audit Logs" />
            <Tooltip title="View covered operations" arrow>
              <IconButton
                onClick={() => setCoverageOpen(true)}
                size="small"
                sx={{ color: "#f5751D" }}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handleClearOpen}
            sx={{ textTransform: "none" }}
          >
            Clear Logs
          </Button>
        </Box>

        {/* Filters row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "end",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "end", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>From Date:</Typography>
              <Datetime
                className="datefield_select frm_date m-0 new_input"
                value={fromDate}
                onChange={(val) => setFromDate(val)}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>To Date:</Typography>
              <Datetime
                className="datefield_select new_input m-0"
                value={toDate}
                onChange={(val) => setToDate(val)}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
              />
            </Box>
            <Select
              size="small"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              {ENTITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              {ACTIONS.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ width: 130 }}
            />
            <Box sx={{ display: "flex" }}>
              <IconButton
                className="filter_search_btn"
                style={{ marginLeft: "0 !important", background: "green !important" }}
                onClick={handleSearch}
              >
                Search&nbsp;
                <SearchIcon />
              </IconButton>
              <IconButton
                className="filter_reset_btn"
                style={{ marginLeft: "0 !important", backgroundColor: "black" }}
                onClick={handleReset}
              >
                Reset&nbsp;
                <RestartAltIcon />
              </IconButton>
              <Tooltip title={rows.length === 0 ? "No data to export" : `Export ${rows.length} row(s) to CSV`} arrow>
                <span>
                  <IconButton
                    className="filter_search_btn"
                    style={{ marginLeft: "0 !important" }}
                    onClick={handleExportCSV}
                    disabled={rows.length === 0}
                  >
                    Export&nbsp;
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Data Grid */}
        <ThemeProvider theme={MuiTheme}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            density="compact"
            onRowClick={handleRowClick}
            slots={{ toolbar: CustomToolbar }}
            sx={{
              cursor: "pointer",
              // Lock header bg + text color in the component itself.
              // adminstyle.css/style.css rely on hash-suffixed class
              // selectors that drift on MUI rebuilds — when they miss, headers
              // render dark-on-dark (or white-on-white) and become invisible.
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
                backgroundColor: "#0a140e !important",
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                color: "#ffffff !important",
                fontWeight: "bold",
              },
            }}
          />
        </ThemeProvider>
      </Box>

      {/* Coverage Info Dialog */}
      <Dialog
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Operations Tracked by Portal Audit Log</span>
          <IconButton onClick={() => setCoverageOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "gray" }}>
            Every row captures the acting user, source IP, timestamp, and before/after state (where applicable). Use this as proof when an action is disputed.
          </Typography>
          {AUDIT_COVERAGE.map((section) => (
            <Box key={section.category} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  borderLeft: `4px solid ${section.color}`,
                  pl: 1,
                  mb: 1,
                  color: section.color,
                }}
              >
                {section.category}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "15%", color: "#212121 !important" }}>Entity</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "35%", color: "#212121 !important" }}>Actions</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#212121 !important" }}>What's tracked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.items.map((item, idx) => (
                    <TableRow key={`${section.category}-${idx}`}>
                      <TableCell>
                        <Chip label={item.entity} size="small" variant="filled" />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace", color: "#212121 !important" }}>
                        {item.actions}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem", color: "#212121 !important" }}>{item.desc}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Before/After Diff Dialog */}
      <Dialog
        open={diffDialogOpen}
        onClose={() => setDiffDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>
            Change Details{" "}
            {selectedRow && (
              <>
                <Chip label={selectedRow.action} size="small" color={ACTION_COLORS[selectedRow.action] || "default"} sx={{ ml: 1 }} />
                <Chip label={selectedRow.entity_type} size="small" sx={{ ml: 1 }} />
              </>
            )}
          </span>
          <IconButton onClick={() => setDiffDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: "gray" }}>
                {selectedRow.description}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#212121 !important" }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#d32f2f !important" }}>Before</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#2e7d32 !important" }}>After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parseDiffData(selectedRow).map((row) => (
                    <TableRow key={row.field}>
                      <TableCell sx={{ fontWeight: 500, color: "#212121 !important" }}>{row.field}</TableCell>
                      <TableCell sx={{ backgroundColor: "#ffebee", color: "#d32f2f" }}>
                        {row.before}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                        {row.after}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Clear Logs Confirmation Dialog */}
      <Dialog
        open={clearOpen}
        onClose={handleClearClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#d32f2f",
            fontWeight: 600,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DeleteForeverIcon />
            Clear Portal Audit Logs
          </span>
          <IconButton onClick={handleClearClose} disabled={clearLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>This action cannot be undone.</strong> All portal audit log
            entries with a timestamp <strong>before</strong> the date below will
            be permanently deleted from the database.
          </Alert>

          <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.5, color: "#212121 !important" }}>
            Delete entries created before:
          </Typography>
          <Datetime
            className="datefield_select new_input m-0"
            value={clearBeforeDate}
            onChange={(val) => setClearBeforeDate(val)}
            dateFormat="DD/MM/YYYY"
            timeFormat="HH:mm"
            isValidDate={(current) => current.isSameOrBefore(moment(), "day")}
          />
          {!isClearDateValid && (
            <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
              Pick a valid date in the past or today.
            </Typography>
          )}

          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#fff3e0", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 13, color: "#5d4037 !important" }}>
              Cutoff:{" "}
              <strong>
                {isClearDateValid
                  ? clearBeforeDate.format("DD/MM/YYYY HH:mm:ss")
                  : "—"}
              </strong>
              <br />
              Logs with <code>created_at &lt;</code> this datetime will be
              deleted. The clear operation itself will be recorded as a new
              audit entry (so the trail of who-cleared-what survives).
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 500, mt: 2, mb: 0.5, color: "#212121 !important" }}>
            Type <strong>{CLEAR_CONFIRM_PHRASE}</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={clearConfirmText}
            onChange={(e) => setClearConfirmText(e.target.value)}
            placeholder={CLEAR_CONFIRM_PHRASE}
            disabled={clearLoading}
            error={
              clearConfirmText.length > 0 &&
              clearConfirmText !== CLEAR_CONFIRM_PHRASE
            }
            helperText={
              clearConfirmText.length > 0 &&
              clearConfirmText !== CLEAR_CONFIRM_PHRASE
                ? `Type ${CLEAR_CONFIRM_PHRASE} exactly (case-sensitive).`
                : " "
            }
          />

          {clearError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {clearError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClearClose}
            disabled={clearLoading}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearSubmit}
            disabled={!isClearArmed}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ textTransform: "none" }}
          >
            {clearLoading ? "Deleting…" : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminPortalAuditLogs;
